import { Box, Checkbox, FormControl, FormLabel, ListItemText, MenuItem, Select, Typography, type SelectChangeEvent } from '@mui/material'
import { createElement } from 'react'
import { type ItemValue, ElementFactory, Question, Serializer } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'
import { parseSurveyText } from './components/parseSurveyText'
import { SelectionChip } from '../../SelectionChip'

const CUSTOM_TYPE = 'surveymultiselect'

export class SurveyMultiSelectModel extends Question {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
    this.createItemValues('choices')
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get choices (): ItemValue[] {
    return this.getPropertyValue('choices')
  }

  set choices (val: ItemValue[]) {
    this.setPropertyValue('choices', val)
  }

  get choiceTexts (): Array<{ value: string, text: string }> {
    return this.choices.map((c: ItemValue) => ({
      value: c.value as string,
      text: (c.text ?? c.value)
    }))
  }

  get placeholder (): string {
    return this.getPropertyValue('placeholder') ?? 'Choose an option'
  }

  set placeholder (val: string) {
    this.setPropertyValue('placeholder', val)
  }

  isEmpty (): boolean {
    const val = this.value
    return val == null || !Array.isArray(val) || val.length === 0
  }
}

export function registerMultiSelectQuestion (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyMultiSelectModel(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [{
      name: 'choices',
      type: 'itemvalue[]',
      category: 'general'
    }, {
      name: 'placeholder',
      type: 'text',
      category: 'general'
    }],
    function () {
      return new SurveyMultiSelectModel('')
    },
    'question'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionMultiSelect, props)
  })
}

export class SurveyQuestionMultiSelect extends SurveyQuestionElementBase {
  get question (): SurveyMultiSelectModel {
    return this.questionBase as SurveyMultiSelectModel
  }

  setSelected = (selected: string[]): void => {
    this.question.value = selected.length > 0 ? selected : undefined
  }

  handleChange = (event: SelectChangeEvent<string[]>): void => {
    const value = event.target.value
    this.setSelected(typeof value === 'string' ? value.split(',') : value)
  }

  removeValue = (choiceValue: string): void => {
    const current: string[] = Array.isArray(this.question.value) ? this.question.value : []
    this.setSelected(current.filter((v) => v !== choiceValue))
  }

  renderElement (): JSX.Element {
    const selectedValues: string[] = Array.isArray(this.question.value) ? this.question.value : []
    const textByValue = new Map(this.question.choiceTexts.map((c) => [c.value, c.text]))
    const orderByValue = new Map(this.question.choiceTexts.map((c, index) => [c.value, index]))
    const orderedSelectedValues = [...selectedValues].sort(
      (a, b) => (orderByValue.get(a) ?? 0) - (orderByValue.get(b) ?? 0)
    )

    return (
      <FormControl component="fieldset" fullWidth>
        <FormLabel className='surveyQuestionTitle'>
          {this.question.fullTitle}
        </FormLabel>
        {this.question.description != null && this.question.description !== '' && (
          <Typography className='surveyQuestionDescription' sx={{ width: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginY: '10px' }}>
            {parseSurveyText(this.question.description)}
          </Typography>
        )}
        <Select
          variant="filled"
          multiple
          displayEmpty
          disabled={this.question.isReadOnly}
          value={selectedValues}
          onChange={this.handleChange}
          data-testid={this.question.name}
          className='surveyQuestionField'
          sx={{ width: '100%', maxWidth: '480px' }}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { sx: { maxHeight: 320 } }
          }}
          renderValue={() =>
            <Box component="span" sx={{ color: 'var(--grey-500)' }}>{this.question.placeholder}</Box>}
        >
          {this.question.choiceTexts.map((choice) => (
            <MenuItem key={choice.value} value={choice.value} data-testid={`${this.question.name}-${choice.value}`}>
              <Checkbox checked={selectedValues.includes(choice.value)} />
              <ListItemText primary={parseSurveyText(choice.text)} />
            </MenuItem>
          ))}
        </Select>
        {selectedValues.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: '8px', columnGap: '24px', mt: '24px' }}>
            {orderedSelectedValues.map((v) => (
              <SelectionChip
                key={v}
                label={textByValue.get(v) ?? v}
                onDelete={this.question.isReadOnly ? undefined : () => { this.removeValue(v) }}
                dataTestId={`${this.question.name}-chip-${v}`}
              />
            ))}
          </Box>
        )}
      </FormControl>
    )
  }
}
